// src/pages/GroupDetail.js
// Updated: 10/06/2025 05:58 PM

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { db } from '../services/firebase';
import {
    doc, onSnapshot, getDoc, deleteDoc, updateDoc, collection,
    query, where, getDocs, arrayUnion, setDoc, serverTimestamp,
    orderBy, addDoc, arrayRemove, writeBatch, orderBy as firestoreOrderBy
} from 'firebase/firestore';
import {
    Container, Typography, CircularProgress, Box, Paper, Button, Dialog,
    DialogTitle, DialogContent, DialogContentText, DialogActions, Tabs,
    Tab, Avatar, List, ListItem, ListItemAvatar, ListItemText, Divider,
    ToggleButtonGroup, ToggleButton, Chip, IconButton, Badge,
    TextField
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import CalendarViewMonthIcon from '@mui/icons-material/CalendarViewMonth';
import ViewListIcon from '@mui/icons-material/ViewList';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import DeleteIcon from '@mui/icons-material/Delete';
import EventDeleteIcon from '@mui/icons-material/DeleteForever';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import ChatIcon from '@mui/icons-material/Chat';
import SendIcon from '@mui/icons-material/Send';
import { Calendar, dateFnsLocalizer, Views } from 'react-big-calendar';
import format from 'date-fns/format';
import parse from 'date-fns/parse';
import startOfWeek from 'date-fns/startOfWeek';
import getDay from 'date-fns/getDay';
import enUS from 'date-fns/locale/en-US';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { rrulestr } from 'rrule';
import GroupPostForm from '../components/GroupPostForm';
import GroupEventModal from '../components/GroupEventModal';
import GroupInviteModal from '../components/GroupInviteModal';
import GroupChat from '../components/GroupChat';

const locales = { 'en-US': enUS };
const localizer = dateFnsLocalizer({ format, parse, startOfWeek, getDay, locales });

function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`group-tabpanel-${index}`}
      aria-labelledby={`group-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function GroupDetail() {
    const { groupId } = useParams();
    const { currentUser, userProfile, churchProfile } = useAuth();
    const navigate = useNavigate();
    const [group, setGroup] = useState(null);
    const [announcements, setAnnouncements] = useState([]);
    const [groupEvents, setGroupEvents] = useState([]);
    const [members, setMembers] = useState([]);
    const [joinRequests, setJoinRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState(0);
    const [isConfirmOpen, setIsConfirmOpen] = useState(false);
    const [isEventModalOpen, setIsEventModalOpen] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [selectedOccurrenceStart, setSelectedOccurrenceStart] = useState(null);
    const [eventViewMode, setEventViewMode] = useState('list');
    const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
    const [allChurchMembers, setAllChurchMembers] = useState([]);
    const [isEventDetailsOpen, setIsEventDetailsOpen] = useState(false);
    const [eventToDisplay, setEventToDisplay] = useState(null);
    const [isRemoveMemberConfirmOpen, setIsRemoveMemberConfirmOpen] = useState(false);
    const [memberToRemove, setMemberToRemove] = useState(null);
    const [calendarDate, setCalendarDate] = useState(new Date());
    const [calendarView, setCalendarView] = useState(Views.MONTH);
    const [unreadGroupMessageCount, setUnreadGroupMessageCount] = useState(0);

    const [newCommentText, setNewCommentText] = useState({});
    const [commentsByAnnouncement, setCommentsByAnnouncement] = useState({});
    const [submittingComment, setSubmittingComment] = useState(false);

    const isGroupLeader = group?.leaderId === currentUser?.uid;
    const isChurchAdmin = userProfile?.role === 'Church Administrator' || userProfile?.role === 'Super Administrator';
    const isLeadership = userProfile?.role === 'Leadership';
    const canManageGroup = isGroupLeader || isChurchAdmin;
    const canManageEvents = canManageGroup || isLeadership;
    const isMember = group?.memberUids?.includes(currentUser?.uid);
    const canViewContent = isMember || canManageGroup;

    useEffect(() => {
      if (!userProfile?.churchId || !groupId || !currentUser?.uid) {
        setLoading(false);
        return;
      }
      const groupDocRef = doc(db, 'churches', userProfile.churchId, 'groups', groupId);
      const unsubscribe = onSnapshot(groupDocRef, (docSnap) => {
        if (docSnap.exists()) {
          const groupData = { id: docSnap.id, ...docSnap.data() };
          setGroup(groupData);
        } else {
          console.error("Group not found!");
        }
        setLoading(false);
      });

      return () => unsubscribe();
    }, [userProfile, groupId, currentUser]);

    useEffect(() => {
        if (!group || !canViewContent || activeTab !== 0) return;

        const postsColRef = collection(db, 'churches', userProfile.churchId, 'groups', groupId, 'groupAnnouncements');
        const q = query(postsColRef, orderBy('createdAt', 'desc'));

        const unsubAnnouncements = onSnapshot(q, async (snapshot) => {
            const fetchedAnnouncements = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setAnnouncements(fetchedAnnouncements);
            
            const commentUnsubscribes = [];
            
            for (const ann of fetchedAnnouncements) {
                const commentsRef = collection(db, 'churches', userProfile.churchId, 'groups', groupId, 'groupAnnouncements', ann.id, 'comments');
                const commentsQuery = query(commentsRef, orderBy('timestamp', 'asc'));
                
                const unsub = onSnapshot(commentsQuery, (commentsSnapshot) => {
                    const commentsForAnn = commentsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                    setCommentsByAnnouncement(prev => ({ ...prev, [ann.id]: commentsForAnn }));
                });
                commentUnsubscribes.push(unsub);
            }
            
            return () => commentUnsubscribes.forEach(unsub => unsub());

        });

        return () => unsubAnnouncements();
    }, [group, canViewContent, activeTab, userProfile.churchId, groupId]);


    useEffect(() => {
      if (!group || !canViewContent) return;
      let unsubscribe = () => {};

      if (activeTab === 2) {
        const eventsColRef = collection(db, 'churches', userProfile.churchId, 'groups', groupId, 'groupEvents');
        const q = query(eventsColRef, orderBy('startAt', 'asc'));
        unsubscribe = onSnapshot(q, (snapshot) => {
          const fetchedEvents = snapshot.docs.flatMap(doc => {
            const data = doc.data();
            const start = data.startAt?.toDate();
            const end = data.endAt?.toDate();
            const rruleStr = data.recurrenceRules?.[0];
            const exdates = data.exdates || [];

            if (rruleStr) {
                try {
                    const rrule = rrulestr(rruleStr, { dtstart: start });
                    const now = new Date();
                    const future = new Date();
                    future.setFullYear(now.getFullYear() + 1);
                    const occurrences = rrule.between(now, future);
                    
                    return occurrences.map(occDate => {
                        const exdateString = format(occDate, "yyyyMMdd'T'HHmmss'Z'");
                        if (exdates.includes(exdateString)) {
                            return null;
                        }
                        const duration = end.getTime() - start.getTime();
                        return {
                            ...data,
                            id: doc.id,
                            start: occDate,
                            end: new Date(occDate.getTime() + duration),
                            allDay: data.isAllDay,
                            title: data.title,
                        };
                    }).filter(Boolean);
                } catch(e) { console.error(e); return []; }
            } else {
              return [{ id: doc.id, ...data, start, end, allDay: data.isAllDay, title: data.title }];
            }
          });
          setGroupEvents(fetchedEvents.filter(e => e.start instanceof Date));
        });
      } else if (activeTab === 3 && group.memberUids?.length > 0) {
        const fetchMembers = async () => {
          const validMemberUids = group.memberUids.filter(uid => typeof uid === 'string' && uid.trim() !== '');
          const memberPromises = validMemberUids.map(uid => getDoc(doc(db, 'users', uid)));
          
          const settledMemberDocs = await Promise.allSettled(memberPromises);
          
          const memberList = settledMemberDocs
              .filter(result => result.status === 'fulfilled' && result.value.exists())
              .map(result => ({ id: result.value.id, ...result.value.data() }))
              .sort((a, b) => {
                  const lastNameComparison = a.lastName.localeCompare(b.lastName);
                  if (lastNameComparison !== 0) return lastNameComparison;
                  return a.firstName.localeCompare(b.firstName);
              });

          setMembers(memberList);
        };
        fetchMembers();
      } else if (activeTab === 4 && canManageGroup) {
        const requestsRef = query(collection(db, 'churches', userProfile.churchId, 'groups', groupId, 'joinRequests'), firestoreOrderBy('userName'));
        unsubscribe = onSnapshot(requestsRef, snapshot => { setJoinRequests(snapshot.docs.map(d => ({ id: d.id, ...d.data() }))); });
      }

      if (canManageGroup) {
        const usersColRef = collection(db, 'users');
        const q = query(usersColRef, where('churchId', '==', userProfile.churchId), firestoreOrderBy('lastName'), firestoreOrderBy('firstName'));
        getDocs(q).then(snapshot => { setAllChurchMembers(snapshot.docs.map(d => d.data())); });
      }

      return () => unsubscribe();
    }, [group, canViewContent, canManageGroup, userProfile, groupId, activeTab]);
    
    useEffect(() => {
      if (!group?.chatInfo?.lastMessageAt || !currentUser?.uid) return;
      const lastReadTimestamp = group.chatInfo.lastRead?.[currentUser.uid];
      const lastMessageTimestamp = group.chatInfo.lastMessageAt;

      if (!lastReadTimestamp || lastReadTimestamp.toMillis() < lastMessageTimestamp.toMillis()) {
        const chatRef = collection(db, 'churches', userProfile.churchId, 'groups', groupId, 'groupChat');
        const q = query(chatRef, orderBy('sentAt', 'desc'), where('sentAt', '>', lastReadTimestamp || new Date(0)));
        const unsubscribe = onSnapshot(q, snapshot => {
          const unreadCount = snapshot.docs.filter(doc => doc.data().sentAt.toMillis() > (lastReadTimestamp?.toMillis() || 0)).length;
          setUnreadGroupMessageCount(unreadCount);
        });
        return () => unsubscribe();
      } else {
          setUnreadGroupMessageCount(0);
      }
    }, [group, currentUser, userProfile.churchId, groupId]);

    const handleMarkChatAsRead = useCallback(async () => {
      if (!currentUser?.uid || !userProfile?.churchId || !groupId) return;
      const groupDocRef = doc(db, 'churches', userProfile.churchId, 'groups', groupId);
      try {
        await updateDoc(groupDocRef, { [`chatInfo.lastRead.${currentUser.uid}`]: serverTimestamp() });
        setUnreadGroupMessageCount(0);
      } catch (error) {
        console.error("Error marking chat as read:", error);
      }
    }, [currentUser?.uid, userProfile?.churchId, groupId]);

    const handleTabChange = (event, newValue) => {
      setActiveTab(newValue);
      if (newValue === 1) {
        handleMarkChatAsRead();
      }
    };
    
    const handleDelete = async () => { if (window.confirm('Are you sure you want to permanently delete this group?')) { try { await deleteDoc(doc(db, 'churches', userProfile.churchId, 'groups', groupId)); navigate('/groups'); } catch (error) { console.error("Error deleting group:", error); alert("Failed to delete group. Check permissions."); } } };
    
    const handleRequestToJoin = async () => { 
        try { 
            const requestRef = doc(db, 'churches', userProfile.churchId, 'groups', groupId, 'joinRequests', currentUser.uid); 
            await setDoc(requestRef, { userName: `${userProfile.firstName} ${userProfile.lastName}`, userId: currentUser.uid, requestedAt: serverTimestamp() }); 
            alert('Your request to join has been sent!'); 
        } catch (error) { 
            console.error("Error sending join request:", error); 
            alert("Failed to send join request."); 
        } 
    };
    
    const handleApproveRequest = async (request) => { 
      try { 
        const groupDocRef = doc(db, 'churches', userProfile.churchId, 'groups', groupId); 
        await updateDoc(groupDocRef, { memberUids: arrayUnion(request.userId) }); 
        await deleteDoc(doc(db, 'churches', userProfile.churchId, 'groups', groupId, 'joinRequests', request.userId)); 
      } catch (error) { 
        console.error("Error approving request:", error); 
        alert("Failed to approve request."); 
      } 
    };
    const handleDenyRequest = async (request) => { 
      try { 
        await deleteDoc(doc(db, 'churches', userProfile.churchId, 'groups', groupId, 'joinRequests', request.userId)); 
      } catch (error) { 
        console.error("Error denying request:", error); 
        alert("Failed to deny request."); 
      } 
    };
    const handleEventSave = async (eventData, eventId) => { try { const eventsColRef = collection(db, 'churches', userProfile.churchId, 'groups', groupId, 'groupEvents'); const dataToSave = { ...eventData, churchId: userProfile.churchId, groupName: group.name }; if (eventId) { await updateDoc(doc(eventsColRef, eventId), { ...dataToSave, updatedAt: serverTimestamp() }); } else { await addDoc(eventsColRef, { ...dataToSave, createdAt: serverTimestamp() }); } setIsEventModalOpen(false); setSelectedEvent(null); setSelectedOccurrenceStart(null); } catch (error) { console.error("Error saving event:", error); alert("Failed to save event."); } };
    const handleInviteMembers = async (uidsToInvite) => { 
      if (!uidsToInvite || uidsToInvite.length === 0) return; 
      const groupDocRef = doc(db, 'churches', userProfile.churchId, 'groups', groupId); 
      try { 
        await updateDoc(groupDocRef, { memberUids: arrayUnion(...uidsToInvite) }); 
        alert('Members invited successfully!'); 
      } catch (error) { 
        console.error("Error inviting members: ", error); 
        alert("Failed to invite members."); 
      } 
    };
    const handleEventClick = useCallback((event) => { setEventToDisplay(event); setIsEventDetailsOpen(true); }, []);
    const handleCloseEventDetails = useCallback(() => { setIsEventDetailsOpen(false); setEventToDisplay(null); }, []);
    
    const handleDeleteEvent = async (eventId, occurrenceToDelete = null) => {
        if (!userProfile?.churchId || !groupId || !eventId) return;

        const eventDocRef = doc(db, 'churches', userProfile.churchId, 'groups', groupId, 'groupEvents', eventId);

        try {
            if (occurrenceToDelete) {
                const eventDoc = await getDoc(eventDocRef);
                const masterEventData = eventDoc.data();

                if (masterEventData && masterEventData.recurrenceRules && masterEventData.recurrenceRules.length > 0) {
                    if (!window.confirm("Are you sure you want to delete ONLY this occurrence?")) {
                        return;
                    }
                    const exdateString = format(occurrenceToDelete, "yyyyMMdd'T'HHmmss'Z'");
                    await updateDoc(eventDocRef, {
                        exdates: arrayUnion(exdateString)
                    });
                    alert("Event occurrence deleted!");
                } else {
                    if (!window.confirm('This is not a recurring event. Delete the event?')) {
                        return;
                    }
                    await deleteDoc(eventDocRef);
                    alert('Event deleted successfully!');
                }
            } else {
                if (!window.confirm('Are you sure you want to delete this entire event series?')) {
                    return;
                }
                await deleteDoc(eventDocRef);
                alert('Event deleted successfully!');
            }
        } catch (error) {
            console.error("Error during event deletion:", error);
            alert(`Failed to delete event/occurrence: ${error.message}`);
        }

        handleCloseEventDetails();
        setIsEventModalOpen(false);
        setSelectedEvent(null);
        setSelectedOccurrenceStart(null);
    };

    const formatDisplayDate = (date, allDay) => { if (!date) return 'Not specified'; const options = allDay ? { dateStyle: 'medium', timeZone: 'UTC' } : { dateStyle: 'medium', timeStyle: 'short' }; return new Date(date).toLocaleString([], options); };
    const handleAddToCalendar = (event) => { if (!event) return; const toICSDate = (date, allDay = false) => { const d = new Date(date); if (allDay) { const year = d.getUTCFullYear(); const month = (d.getUTCMonth() + 1).toString().padStart(2, '0'); const day = d.getUTCDate().toString().padStart(2, '0'); return `${year}${month}${day}`; } else { return d.toISOString().replace(/[-:.]/g, "").slice(0, -4) + 'Z'; } }; const startDateICS = event.startAt?.toDate(); const endDateICS = event.endAt?.toDate() || startDateICS; let dtstartString = `DTSTART:${toICSDate(startDateICS, event.isAllDay)}`; let dtendString = `DTEND:${toICSDate(endDateICS, event.isAllDay)}`; if(event.isAllDay) { dtstartString = `DTSTART;VALUE=DATE:${toICSDate(startDateICS, true)}`; const actualEndDate = new Date(endDateICS); const icsEndDate = new Date(actualEndDate.setDate(actualEndDate.getDate() + 1)); dtendString = `DTEND;VALUE=DATE:${toICSDate(icsEndDate, true)}`; } const icsContent = [ 'BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//Church Connect App//EN', 'BEGIN:VEVENT', `UID:${event.id}@yourapp.com`, `DTSTAMP:${toICSDate(new Date())}`, dtstartString, dtendString, `SUMMARY:${event.title || ''}`, `DESCRIPTION:${event.description || ''}`, `LOCATION:${event.location || ''}`, 'END:VEVENT', 'END:VCALENDAR' ].join('\n'); const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' }); const url = URL.createObjectURL(blob); const link = document.createElement('a'); link.href = url; link.setAttribute('download', `${event.title.replace(/\s/g, '_')}.ics`); document.body.appendChild(link); link.click(); document.body.removeChild(link); URL.revokeObjectURL(url); };
    const handleRemoveMemberRequest = (member) => { setMemberToRemove(member); setIsRemoveMemberConfirmOpen(true); };
    const handleConfirmRemoveMember = async () => {
      if (!memberToRemove || !group?.id || !userProfile?.churchId) return;
      try {
        const groupDocRef = doc(db, 'churches', userProfile.churchId, 'groups', group.id);
        await updateDoc(groupDocRef, { memberUids: arrayRemove(memberToRemove.id) });
        alert(`${memberToRemove.firstName} ${memberToRemove.lastName} has been removed from the group.`);
        setIsRemoveMemberConfirmOpen(false);
        setMemberToRemove(null);
      } catch (error) {
        console.error("Error removing member:", error);
        alert("Failed to remove member. Check permissions.");
      }
    };
    const handleCloseRemoveMemberConfirm = () => { setIsRemoveMemberConfirmOpen(false); setMemberToRemove(null); };
    const eventPropGetter = useCallback( (event, start, end, isSelected) => { let style = {}; if (event.isGroupEvent) { if (event.showOnMainCalendar) { style = { backgroundColor: '#3174ad', borderColor: '#2a6496', color: 'white' }; } else { style = { backgroundColor: '#468847', borderColor: '#356635', color: 'white' }; } } style.borderRadius = '5px'; style.opacity = 0.8; style.display = 'block'; return { style }; }, [] );
    
    const handlePostComment = async (announcementId) => {
        if (!newCommentText[announcementId]?.trim()) return;
        setSubmittingComment(true);
        try {
            const commentsColRef = collection(db, 'churches', userProfile.churchId, 'groups', groupId, 'groupAnnouncements', announcementId, 'comments');
            await addDoc(commentsColRef, {
                authorUid: currentUser.uid,
                authorName: `${userProfile.firstName} ${userProfile.lastName}`,
                text: newCommentText[announcementId].trim(),
                timestamp: serverTimestamp(),
                churchId: userProfile.churchId,
                groupId: groupId,
                announcementId: announcementId,
            });
            setNewCommentText(prev => ({...prev, [announcementId]: '' }));
        } catch (error) {
            console.error("Error posting comment:", error);
            alert("Failed to post comment.");
        } finally {
            setSubmittingComment(false);
        }
    };
    
    const handleDeletePost = async (postId) => {
        if (!window.confirm("Are you sure you want to delete this post? This action cannot be undone.")) {
            return;
        }

        try {
            const commentsRef = collection(db, 'churches', userProfile.churchId, 'groups', groupId, 'groupAnnouncements', postId, 'comments');
            const commentsSnapshot = await getDocs(commentsRef);
            const batch = writeBatch(db);
            commentsSnapshot.docs.forEach(doc => {
                batch.delete(doc.ref);
            });
            await batch.commit();

            const postRef = doc(db, 'churches', userProfile.churchId, 'groups', groupId, 'groupAnnouncements', postId);
            await deleteDoc(postRef);

        } catch (error) {
            console.error("Error deleting post and comments:", error);
            alert("Failed to delete post.");
        }
    };


    const futureEvents = groupEvents
        .filter(event => (event.end ? event.end >= new Date() : event.start >= new Date()))
        .sort((a, b) => a.start - b.start);

    const calendarEvents = groupEvents.map(event => ({ ...event, start: event.start, end: event.end }));

    const [searchParams] = useSearchParams();
    useEffect(() => {
      const tabParam = searchParams.get('tab');
      if (tabParam === 'chat') {
        setActiveTab(1);
        handleMarkChatAsRead();
      }
    }, [searchParams, handleMarkChatAsRead]);


    if (loading) return <CircularProgress />;
    if (!group) return <Typography>Group not found.</Typography>;

    return (
        <Container>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="h4">{group.name}</Typography>
                {!group.isPublic && <Chip label="Private" size="small" variant="outlined" sx={{ ml: 2 }} />}
              </Box>
              {canManageGroup &&
                <Box>
                  <Button variant="outlined" startIcon={<PersonAddIcon />} onClick={() => setIsInviteModalOpen(true)}>Invite</Button>
                  <Button variant="outlined" startIcon={<EditIcon />} sx={{ ml: 2 }} onClick={() => navigate(`/groups/${groupId}/edit`)}>Edit</Button>
                  {isChurchAdmin && (
                    <Button variant="contained" color="error" startIcon={<DeleteIcon />} sx={{ ml: 2 }} onClick={() => setIsConfirmOpen(true)}>Delete</Button>
                  )}
                </Box>
              }
            </Box>
            {group.leaderName && (<Typography variant="subtitle1" color="text.secondary" gutterBottom>Led by: {group.leaderName}</Typography>)}
            <Typography variant="body1" paragraph>{group.description}</Typography>

            {canViewContent ? (
                <>
                    <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                        <Tabs value={activeTab} onChange={handleTabChange} aria-label="group detail tabs">
                            <Tab label="Wall" id="group-tab-0" aria-controls="group-tabpanel-0" />
                            <Tab 
                                id="group-tab-1" 
                                aria-controls="group-tabpanel-1"
                                icon={<ChatIcon />} 
                                iconPosition="start"
                                label={
                                    <Badge color="error" variant="dot" invisible={unreadGroupMessageCount === 0}>
                                        Chat
                                    </Badge>
                                } 
                            />
                            <Tab label="Events" id="group-tab-2" aria-controls="group-tabpanel-2" />
                            <Tab label="Members" id="group-tab-3" aria-controls="group-tabpanel-3" />
                            {canManageGroup && !group.isPublic && <Tab label={`Requests (${joinRequests.length})`} id="group-tab-4" aria-controls="group-tabpanel-4" />}
                        </Tabs>
                    </Box>

                    <TabPanel value={activeTab} index={0}>
                        {canManageGroup && <GroupPostForm groupId={groupId} />}
                        <List sx={{ mt: 2 }}>
                            {announcements.length > 0 ? (
                                announcements.map(post => (
                                    <Paper key={post.id} sx={{ p: 2, mb: 2 }}>
                                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                        <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>{post.text}</Typography>
                                        {(isChurchAdmin || isGroupLeader) && (
                                            <IconButton size="small" onClick={() => handleDeletePost(post.id)}>
                                                <DeleteIcon fontSize="small" color="error" />
                                            </IconButton>
                                        )}
                                      </Box>
                                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>- {post.authorName} on {post.createdAt?.toDate().toLocaleDateString()}</Typography>

                                        <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #eee' }}>
                                            <Typography variant="subtitle2" gutterBottom>Comments</Typography>
                                            <List dense>
                                                {commentsByAnnouncement[post.id]?.length > 0 ? (
                                                    commentsByAnnouncement[post.id].map(comment => (
                                                        <ListItem key={comment.id} disablePadding>
                                                            <ListItemAvatar>
                                                                <Avatar sx={{ width: 24, height: 24, fontSize: '0.8rem' }}>{comment.authorName?.charAt(0)}</Avatar>
                                                            </ListItemAvatar>
                                                            <ListItemText
                                                                primary={
                                                                    <Typography component="span" variant="body2" color="text.primary">
                                                                        <strong>{comment.authorName}</strong>: {comment.text}
                                                                    </Typography>
                                                                }
                                                                secondary={comment.timestamp ? format(comment.timestamp.toDate(), 'MMM d, h:mm a') : 'Just now'}
                                                            />
                                                        </ListItem>
                                                    ))
                                                ) : (
                                                    <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>No comments yet.</Typography>
                                                )}
                                            </List>

                                            {isMember && (
                                                <Box sx={{ display: 'flex', mt: 2 }}>
                                                    <TextField
                                                        fullWidth
                                                        variant="outlined"
                                                        size="small"
                                                        placeholder="Add a comment..."
                                                        value={newCommentText[post.id] || ''}
                                                        onChange={(e) => setNewCommentText(prev => ({...prev, [post.id]: e.target.value}))}
                                                        onKeyPress={(e) => {
                                                            if (e.key === 'Enter' && !submittingComment) {
                                                                handlePostComment(post.id);
                                                            }
                                                        }}
                                                    />
                                                    <Button
                                                        variant="contained"
                                                        sx={{ ml: 1, px: 3 }}
                                                        onClick={() => handlePostComment(post.id)}
                                                        disabled={submittingComment || !newCommentText[post.id]?.trim()}
                                                    >
                                                        {submittingComment ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                                                    </Button>
                                                </Box>
                                            )}
                                        </Box>

                                    </Paper>
                                ))
                            ) : ( <Typography sx={{ p: 2 }}>No posts on the wall yet.</Typography> )}
                        </List>
                    </TabPanel>
                    
                    <TabPanel value={activeTab} index={1}>
                        <GroupChat groupId={groupId} churchId={userProfile.churchId} />
                    </TabPanel>

                    <TabPanel value={activeTab} index={2}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                          <ToggleButtonGroup value={eventViewMode} exclusive onChange={(e, newMode) => { if (newMode) setEventViewMode(newMode); }}>
                            <ToggleButton value="list"><ViewListIcon /></ToggleButton>
                            <ToggleButton value="calendar"><CalendarViewMonthIcon /></ToggleButton>
                          </ToggleButtonGroup>
                          {canManageEvents && ( <Button startIcon={<AddIcon />} variant="contained" onClick={() => { setSelectedEvent(null); setIsEventModalOpen(true); }}>Create Event</Button> )}
                        </Box>
                        {eventViewMode === 'list' ? (
                          <List>
                            {futureEvents.length > 0 ? (
                              futureEvents.map(event => (
                                <ListItem key={`${event.id}-${event.start.toISOString()}`} onClick={() => handleEventClick(event)} sx={{ cursor: 'pointer' }} secondaryAction={ canManageEvents ? (
                                    <Button
                                        size="small"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setSelectedOccurrenceStart(event.start); 
                                            setSelectedEvent({ ...event, startDateTime: event.start, endDateTime: event.end, });
                                            setIsEventModalOpen(true);
                                        }}>
                                        Edit
                                    </Button>
                                ) : null }>
                                  <ListItemText primary={event.title} secondary={`${event.start.toLocaleString()}${event.showOnMainCalendar ? ' (Public on Main Calendar)' : ''}`} />
                                </ListItem>
                              ))
                            ) : ( <Typography sx={{ p: 2 }}>No upcoming events scheduled.</Typography> )}
                          </List>
                        ) : (
                          <Paper sx={{ height: '70vh' }}>
                            <Calendar localizer={localizer} events={calendarEvents} startAccessor="start" endAccessor="end" allDayAccessor="allDay" style={{ height: '100%' }} onSelectEvent={handleEventClick} date={calendarDate} view={calendarView} onNavigate={setCalendarDate} onView={setCalendarView} views={[Views.MONTH, Views.WEEK, Views.DAY, Views.AGENDA]} eventPropGetter={eventPropGetter} />
                          </Paper>
                        )}
                    </TabPanel>
                    
                    <TabPanel value={activeTab} index={3}>
                        <List>
                          {members.length > 0 ? (
                            members.map((member, i) => (
                              <React.Fragment key={member.id}>
                                <ListItem secondaryAction={ canManageGroup && member.id !== currentUser.uid && member.id !== group.leaderId && member.role !== 'Church Administrator' && member.role !== 'Super Administrator' ? ( <IconButton edge="end" aria-label="remove" onClick={() => handleRemoveMemberRequest(member)}><RemoveCircleOutlineIcon /></IconButton> ) : null }>
                                  <ListItemAvatar><Avatar src={member.profilePhotoUrl || ''}>{!member.profilePhotoUrl && member.firstName.charAt(0)}</Avatar></ListItemAvatar>
                                  <ListItemText primary={`${member.firstName} ${member.lastName}`} />
                                </ListItem>
                                {i < members.length - 1 && <Divider />}
                              </React.Fragment>
                            ))
                          ) : ( <Typography sx={{ p: 2 }}>No members in this group yet.</Typography> )}
                        </List>
                    </TabPanel>

                    {canManageGroup && !group.isPublic && (
                        <TabPanel value={activeTab} index={4}>
                          <List>
                            {joinRequests.length > 0 ? (
                              joinRequests.map(req => (
                                <ListItem key={req.id} secondaryAction={ <Box><Button onClick={() => handleApproveRequest(req)}>Approve</Button><Button color="error" onClick={() => handleDenyRequest(req)}>Deny</Button></Box> }>
                                  <ListItemText primary={req.userName} />
                                </ListItem>
                              ))
                            ) : ( <Typography sx={{ p: 2 }}>No pending requests.</Typography> )}
                          </List>
                        </TabPanel>
                    )}

                </>
            ) : (
                <Paper sx={{ p: 3, mt: 3, textAlign: 'center' }}>
                    <Typography variant="h6">This is a private group.</Typography>
                    <Button variant="contained" onClick={handleRequestToJoin} sx={{ mt: 2 }}>Request to Join</Button>
                </Paper>
            )}

            <GroupEventModal open={isEventModalOpen} onClose={() => setIsEventModalOpen(false)} onSave={handleEventSave} onDelete={handleDeleteEvent} event={selectedEvent} isLeadership={canManageEvents} selectedOccurrenceStart={selectedOccurrenceStart} churchProfile={churchProfile} />
            <GroupInviteModal open={isInviteModalOpen} onClose={() => setIsInviteModalOpen(false)} onInvite={handleInviteMembers} allChurchMembers={allChurchMembers} currentGroupMembers={group.memberUids || []} />
            <Dialog open={isConfirmOpen} onClose={() => setIsConfirmOpen(false)}><DialogTitle>Delete Group?</DialogTitle><DialogContent><DialogContentText>Are you sure you want to permanently delete the "{group.name}" group?</DialogContentText></DialogContent><DialogActions><Button onClick={() => setIsConfirmOpen(false)}>Cancel</Button><Button onClick={handleDelete} color="error">Confirm Delete</Button></DialogActions></Dialog>
            {eventToDisplay && ( <Dialog open={isEventDetailsOpen} onClose={handleCloseEventDetails}><DialogTitle>{eventToDisplay.title}</DialogTitle><DialogContent><Typography variant="subtitle1" color="text.secondary">{eventToDisplay.isAllDay ? 'All Day:' : ''} {formatDisplayDate(eventToDisplay.start, eventToDisplay.isAllDay)}{eventToDisplay.end && eventToDisplay.start.getTime() !== eventToDisplay.end.getTime() && ` - ${formatDisplayDate(eventToDisplay.end, eventToDisplay.isAllDay)}`}</Typography>{eventToDisplay.location && ( <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Location: {eventToDisplay.location}</Typography> )}{eventToDisplay.description && ( <Typography variant="body1" sx={{ mt: 2, whiteSpace: 'pre-wrap' }}>{eventToDisplay.description}</Typography> )}{eventToDisplay.showOnMainCalendar && ( <Chip label="Visible on Main Calendar" size="small" variant="outlined" sx={{ mt: 2 }} /> )}</DialogContent><DialogActions><Button onClick={() => handleAddToCalendar(eventToDisplay)} startIcon={<CalendarMonthIcon />} sx={{ mr: 1 }}>Add to Calendar</Button>{canManageEvents && ( <> <Button onClick={() => { setSelectedOccurrenceStart(eventToDisplay.start); setSelectedEvent({ ...eventToDisplay, startDateTime: eventToDisplay.start, endDateTime: eventToDisplay.end, }); setIsEventModalOpen(true); setIsEventDetailsOpen(false); }} startIcon={<EditIcon />}>Edit</Button>
            {eventToDisplay?.recurrenceRules?.length > 0 ? (
              <Button onClick={() => handleDeleteEvent(eventToDisplay.id, eventToDisplay.start)} color="error" startIcon={<EventDeleteIcon />}>Delete Occurrence</Button>
            ) : (
              <Button onClick={() => handleDeleteEvent(eventToDisplay.id)} color="error" startIcon={<EventDeleteIcon />}>Delete Event</Button>
            )}
             </> )}<Button onClick={handleCloseEventDetails}>Close</Button></DialogActions></Dialog> )}
            <Dialog open={isRemoveMemberConfirmOpen} onClose={handleCloseRemoveMemberConfirm}><DialogTitle>Remove Member?</DialogTitle><DialogContent><DialogContentText>Are you sure you want to remove {memberToRemove?.firstName} {memberToRemove?.lastName} from this group?</DialogContentText></DialogContent><DialogActions><Button onClick={handleCloseRemoveMemberConfirm}>Cancel</Button><Button onClick={handleConfirmRemoveMember} color="error">Confirm Remove</Button></DialogActions></Dialog>
        </Container>
    );
}

export default GroupDetail;